import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FiGitBranch, FiArrowRight, FiCheckCircle, FiAlertTriangle,
  FiLock, FiUnlock, FiSearch, FiLink
} from 'react-icons/fi';
import PublicPageHeader from '../../../components/common/PublicPageHeader';
import PublicPageFooter from '../../../components/common/PublicPageFooter';
import DocsSidebar from '../../../components/docs/DocsSidebar';

const DependenciesDocs = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <PublicPageHeader />
      
      <div className="flex">
        <DocsSidebar currentPath="/docs/features/dependencies" />
        
        <main className="flex-1 max-w-4xl mx-auto px-6 py-12">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400 mb-8">
            <Link to="/docs" className="hover:text-blue-600">Docs</Link>
            <span>/</span>
            <Link to="/docs/features" className="hover:text-blue-600">Features</Link>
            <span>/</span>
            <span className="text-gray-900 dark:text-white">Task Dependencies</span>
          </nav>

          {/* Header */}
          <div className="mb-12">
            <div className="inline-flex p-2 bg-violet-100 dark:bg-violet-900/30 rounded-xl mb-4">
              <FiGitBranch className="w-6 h-6 text-violet-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Task Dependencies</h1>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Define relationships between tasks to ensure work is completed in the right order. 
              Tasklina enforces dependencies automatically — preventing tasks from progressing 
              until their blockers are resolved.
            </p>
          </div>

          {/* Overview */}
          <section className="mb-16">
            <div className="bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Keep Work on Track</h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                In complex projects, some tasks must be completed before others can begin. 
                Dependencies make these relationships explicit and enforceable, preventing 
                mistakes and keeping your team moving in the right direction.
              </p>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-violet-600 mb-1">4</div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">Dependency Types</div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 text-center">
                  <FiLock className="w-6 h-6 text-violet-600 mx-auto mb-1" />
                  <div className="text-sm text-gray-600 dark:text-gray-300">Auto-Enforced</div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 text-center">
                  <FiSearch className="w-6 h-6 text-violet-600 mx-auto mb-1" />
                  <div className="text-sm text-gray-600 dark:text-gray-300">Visual Indicators</div>
                </div>
              </div>
            </div>
          </section>

          {/* Dependency Types */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Dependency Types</h2>
            <div className="space-y-4">
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-6">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                    <FiLock className="w-5 h-5 text-red-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Finish-to-Start (FS)</h3>
                  <span className="text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-2 py-0.5 rounded-full">Most Common</span>
                </div>
                <p className="text-gray-600 dark:text-gray-300 mb-3">
                  Task B cannot <strong>start</strong> until Task A <strong>finishes</strong>. 
                  This is the most widely used dependency type.
                </p>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 text-sm text-gray-600 dark:text-gray-300">
                  <strong>Example:</strong> "Implement UI" cannot start until "Design mockups" is done
                </div>
              </div>

              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-6">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                    <FiLink className="w-5 h-5 text-amber-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Start-to-Start (SS)</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-300 mb-3">
                  Task B cannot <strong>start</strong> until Task A <strong>starts</strong>. 
                  Both tasks can run in parallel once the first one begins.
                </p>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 text-sm text-gray-600 dark:text-gray-300">
                  <strong>Example:</strong> "Write tests" can only start once "Write code" has started
                </div>
              </div>

              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-6">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <FiLink className="w-5 h-5 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Finish-to-Finish (FF)</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-300 mb-3">
                  Task B cannot <strong>finish</strong> until Task A <strong>finishes</strong>. 
                  Use when two tasks must wrap up together.
                </p>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 text-sm text-gray-600 dark:text-gray-300">
                  <strong>Example:</strong> "Code review" cannot finish until "Implementation" finishes
                </div>
              </div>

              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-6">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                    <FiLink className="w-5 h-5 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Start-to-Finish (SF)</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-300 mb-3">
                  Task B cannot <strong>finish</strong> until Task A <strong>starts</strong>. 
                  A less common type, typically used for just-in-time scheduling.
                </p>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 text-sm text-gray-600 dark:text-gray-300">
                  <strong>Example:</strong> "Old system decommission" cannot finish until "New system setup" starts
                </div>
              </div>
            </div>
          </section>

          {/* How Dependencies Work */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">How It Works</h2>
            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6">
              <ol className="space-y-3 text-gray-600 dark:text-gray-300">
                <li className="flex items-start space-x-3">
                  <span className="w-6 h-6 bg-violet-600 text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">1</span>
                  <span>Open a task's detail page and find the <strong>Dependencies</strong> section</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="w-6 h-6 bg-violet-600 text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">2</span>
                  <span>Click <strong>Add Dependency</strong> and search for the blocking task</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="w-6 h-6 bg-violet-600 text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">3</span>
                  <span>Select the dependency type (Finish-to-Start is the default)</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="w-6 h-6 bg-violet-600 text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">4</span>
                  <span>Tasklina will show visual indicators for blocked vs. satisfied dependencies</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="w-6 h-6 bg-violet-600 text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">5</span>
                  <span>When all dependencies are satisfied, the task is <strong>unblocked</strong> and can proceed</span>
                </li>
              </ol>
            </div>
          </section>

          {/* Enforcement */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Automatic Enforcement</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="border border-red-200 dark:border-red-800 rounded-xl p-4 bg-red-50 dark:bg-red-900/10">
                <FiAlertTriangle className="w-5 h-5 text-red-500 mb-2" />
                <h4 className="font-medium text-gray-900 dark:text-white mb-1">Blocked</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  If a task's dependencies are not met, you cannot change its status 
                  to In Progress or Done. The system prevents invalid transitions.
                </p>
              </div>
              <div className="border border-green-200 dark:border-green-800 rounded-xl p-4 bg-green-50 dark:bg-green-900/10">
                <FiUnlock className="w-5 h-5 text-green-500 mb-2" />
                <h4 className="font-medium text-gray-900 dark:text-white mb-1">Unblocked</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Once all blocking dependencies are satisfied, the task is automatically 
                  unblocked and its status can be changed freely.
                </p>
              </div>
            </div>
          </section>

          {/* Date Shift Proposals */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Smart Date Suggestions</h2>
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-6">
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                When you add a dependency, Tasklina checks if the dependent task's dates conflict 
                with the blocking task's schedule. If there's a conflict, it automatically 
                suggests shifting the dates to maintain a valid schedule.
              </p>
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-sm text-gray-600 dark:text-gray-300">
                <strong>Example:</strong> If Task A ends on Jan 15 and Task B (finish-to-start) 
                starts on Jan 10, Tasklina will suggest moving Task B's start date to Jan 16.
              </div>
            </div>
          </section>

          {/* Tips */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Best Practices</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <FiCheckCircle className="w-5 h-5 text-green-500 mb-2" />
                <h4 className="font-medium text-gray-900 dark:text-white mb-1">Keep Chains Short</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">Long dependency chains can create bottlenecks. Break work into parallel tracks when possible.</p>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <FiCheckCircle className="w-5 h-5 text-green-500 mb-2" />
                <h4 className="font-medium text-gray-900 dark:text-white mb-1">Use the Right Type</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">Finish-to-Start is most common, but Start-to-Start is better for parallel work.</p>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <FiCheckCircle className="w-5 h-5 text-green-500 mb-2" />
                <h4 className="font-medium text-gray-900 dark:text-white mb-1">Review Regularly</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">Remove stale dependencies as project scope changes to avoid unnecessary blocks.</p>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <FiCheckCircle className="w-5 h-5 text-green-500 mb-2" />
                <h4 className="font-medium text-gray-900 dark:text-white mb-1">Check Before Sprint Start</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">Verify all dependencies are satisfied before assigning tasks in a sprint.</p>
              </div>
            </div>
          </section>

          {/* Related */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Related Features</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <Link
                to="/docs/features/timeline"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Timeline / Gantt View</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
              <Link
                to="/docs/personal/tasks"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Task Management</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
            </div>
          </section>

          {/* Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500 dark:text-gray-400">Last updated: June 2025</div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-500 dark:text-gray-400">Was this helpful?</span>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600">Yes</button>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600">No</button>
              </div>
            </div>
          </div>
        </main>
      </div>

      <PublicPageFooter />
    </div>
  );
};

export default DependenciesDocs;
